// Angular
import { Observable, takeUntil } from 'rxjs';
import { AfterViewInit, Component, EventEmitter, HostListener, Input, OnChanges, OnDestroy, OnInit, Output, QueryList, SimpleChanges, ViewChild, ViewChildren } from '@angular/core';
import { Router } from '@angular/router';

// Libraries
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, MatSortable } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { CdkTableExporter } from 'mat-table-exporter';
import { parseISO } from 'date-fns';

import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { SelectionModel } from '@angular/cdk/collections';

// Services
import { DateRangeService, UtilityService } from '../../services';
import { SubheaderService } from '../subheader/services/subheader.service';
import { TimezoneService } from '../../../modules/job/services/timezone.service';

// Models
import { LineStyle } from '../../../modules/channel/models';
import {
	AppCleanup,
	DateRange,
	DateRangeMillis,
	DynamicDateFilter,
	DynamicMenuButton,
	DynamicTableColumn,
	DynamicTableType,
	LineStyleOption,
	LineStyles,
	LineWeights,
	Paging,
} from '../../models';
import { UtilityHelper } from '../../helpers';
import { MatCheckbox } from '@angular/material/checkbox';
import { DateRangeISOModel } from '../date-time-range-picker/date-time-range-picker.component';

@Component({
	selector: 'dynamic-table',
	templateUrl: './dynamic-table.component.html',
	styleUrls: ['./dynamic-table.component.scss'],
})
export class DynamicTableComponent extends AppCleanup implements OnInit, OnChanges, OnDestroy, AfterViewInit {
	@Input() title: string; // Table title
	@Input() initSpinner: boolean; // Flag hides init spinner
	@Input() showSearch = false; // Shows table search
	@Input() useGlobalSearch: boolean = false; // Uses global subheader search
	@Input() items$: Observable<any[]>;
	@Input() items: any[]; // When using this array as data source, just pushing or removing the array won't update the table. You need to use this approach (this.items = [...items])
	@Input() highlightNewItems: boolean = false; // Flag for highlighting a new item as it is added
	@Input() columnList$: Observable<DynamicTableColumn[]>;
	@Input() columnList: DynamicTableColumn[] = [];
	@Input() addRoute: string; // Route to add entity
	@Input() addExternally: boolean = false; // Flag for handling adding entity externally
	@Input() addMenuButtons: DynamicMenuButton[]; // List of add buttons (dropdown)
	@Input() addMenuButtonsFilter: boolean = false; // Flag for making button dropdown filterable
	@Input() addHeaderButton: boolean = false; // Flag for placing add button as a header for last column. This only works if the action column is present, otherwise will be ignored.
	@Input() hideAddButton: boolean = false; // Hides add button
	@Input() customButtons: DynamicMenuButton[]; // List of custom buttons (not dropdown)
	@Input() customMenuButtons: DynamicMenuButton[]; // List of custom buttons (dropdown) // Functionality has not been added yet
	@Input() menuButtons: DynamicMenuButton[] = []; // Dropdown menu buttons for action column under kebab icon
	@Input() hideMenuButtons: boolean = false; // Hides dropdown menu buttons
	@Input() editExternally: boolean = false; // Flag for handling editing entity externally
	@Input() editRoute: string; // Route to edit entity
	@Input() editColumn: string = 'id'; // Column value for edit route
	@Input() selectable: boolean; // Allows selecting a row and emitting a value, without doing anything else
	@Input() multiselect: boolean; // Allows selecting multiple rows and emitting a value, without doing anything else
	@Input() deleteColumn = 'id'; // Column value for delete route
	@Input() showPaginator: boolean = true; // Shows/hides paginator
	@Input() showRowCount: boolean = false; // Shows/hides row count
	@Input() maxRowCount: number; // Maximum row count
	@Input() defaultPaging: boolean = true;
	@Input() defaultPageSize: number = 10;
	@Input() pageSizeOptions: number[] = [10, 50, 100];
	@Input() paging$: Observable<Paging>;
	@Input() paging: Paging;
	@Input() draggable = false; // Enables drag and drop of rows. If the flag is true, sorting is disabled for table
	@Input() dateRangeSet$: Observable<DateRange>;
	@Input() showDateInput = false;
	@Input() showColumnFilter = false; // Shows column filter. Hides and shows columns
	@Input() dynamicDateFilter: DynamicDateFilter;
	@Input() selectionMinDate: Date;
	@Input() selectionMaxDate: Date;
	@Input() stickyHeaders: boolean = true;
	@Input() stickyFooters: boolean = true;
	@Input() showFatigueDownload: boolean = false;
	@Input() utcDateTimeOffset?: number = null;
	@Input() fitParent: 'always' | 'sm' | 'md' | 'lg' | 'xl';
	@Output() onAdd: EventEmitter<any> = new EventEmitter(); // Triggers external addition
	@Output() onRowClick: EventEmitter<any | any[]> = new EventEmitter<any | any[]>(); // Triggers when row is clicked
	@Output() onDownloadEvent: EventEmitter<DateRangeMillis> = new EventEmitter(); // Triggers when 'download data' is clicked
	@Output() onDownloadFatigueReportEvent: EventEmitter<DateRange> = new EventEmitter(); // Triggers when 'download fatigue data' is clicked
	@Output() onPageChanged: EventEmitter<Paging> = new EventEmitter(); // Triggers when page is changed
	@Output() onDrag: EventEmitter<any> = new EventEmitter(); // Triggers on drag
	@Output() sliderToggled: EventEmitter<any> = new EventEmitter(); // Emits when a slide toggle changed. Needed for showing warning on user channel dialog
	
	searchTerm: string = '';
	lineStyleOptions: Array<LineStyleOption> = [...LineStyles.List];
	lineWeightOptions: Array<number> = [...LineWeights.List];
	filteredItems: any[] = [];
	dataSource = new MatTableDataSource<any>();
	columnNames: string[] = [];
	minDate: Date;
	maxDate: Date;
	dateRange: DateRange = new DateRange();
	showChart = false;
	selection: SelectionModel<any> = new SelectionModel(false, []);
	disableSorting = false;
	showHeader: boolean = false;
	dragEnabled: boolean;
	tableWrapperClass: string;
	footerClass: string;
	showSpinner: boolean;
	showFooter: boolean;
	DynamicTableType = DynamicTableType;
	// ISO UTC properties for new date-time-range-picker
	dateRangeISO: DateRangeISOModel = {
		start: null,
		end: null,
	};
	minDateISO: string;
	maxDateISO: string;
	pickerTimezoneOffset: number = 0;
	dataInitialized: boolean;
	oldItems: any[]; // Copy of items for future comparison. For example, to be able to highlight new items
	readonly exporter: CdkTableExporter;
	readonly dateRangeLocale = {
		displayFormat: 'MMM DD, YYYY HH:mm',
	};

	paginator: MatPaginator;
	sort: MatSort;

	@ViewChild(MatSort) set matSort(sort: MatSort) {
		this.sort = sort;
		this.dataSource.sort = this.sort;
	}

	@ViewChild(MatPaginator) set matPaginator(paginator: MatPaginator) {
		this.paginator = paginator;
	}

	@ViewChildren('multiselectCheckbox') checkboxes!: QueryList<MatCheckbox>;

	hideTooltip: boolean = true;

	@HostListener('window:resize', ['$event'])
	onResize() {
		this.hideTooltip = window.innerWidth > 767;
	}

	constructor(
		private router: Router,
		private subheaderService: SubheaderService,
		private timezoneService: TimezoneService,
		private dateRangeService: DateRangeService,
		private utilityService: UtilityService
	) {
		super();
	}

	ngOnInit() {
		this.utilityService.simulateWindowResize();
	}

	ngOnChanges(changes: SimpleChanges) {
		if (changes?.initSpinner?.currentValue && changes?.initSpinner?.firstChange) {
			this.showSpinner = true;
		}

		if (this.title || this.showDateInput || this.showSearch || this.showColumnFilter || this.customButtons || (!this.hideAddButton && (this.addRoute || this.addExternally || this.addMenuButtons) && !this.addHeaderButton)) {
			this.showHeader = true;
		}

		if (this.dynamicDateFilter?.minDate) {
			this.minDate = new Date(this.dynamicDateFilter.minDate);
			this.minDateISO = this.minDate.toISOString();
		}
		if (this.dynamicDateFilter?.maxDate) {
			this.maxDate = new Date(this.dynamicDateFilter.maxDate);
			this.maxDateISO = this.maxDate.toISOString();
		}

		if (this.paging$) {
			this.paging$.pipe(takeUntil(this.destroy)).subscribe((paging) => {
				if (paging) {
					this.paging = paging;
				} else {
					this.setDefaultPaging();
				}
			});
		} else if (!this.paging) {
			this.setDefaultPaging();
		}

		if (this.columnList$) {
			this.columnList$.pipe(takeUntil(this.destroy)).subscribe((columnList) => {
				this.columnList = columnList || [];
				this.loadDataSource();
			});
		}

		if (changes.items$) {
			this.items$.pipe(takeUntil(this.destroy)).subscribe((items) => {
				this.items = items;
				this.loadDataSource();
			});
		}

		if (changes.items) {
			this.loadDataSource();
		}

		if (this.useGlobalSearch) {
			this.subheaderService.searchChange.pipe(takeUntil(this.destroy)).subscribe((searchTerm) => {
				this.searchTerm = searchTerm;
				this.search();
			});
		}

		if (this.dateRangeSet$) {
			this.dateRangeSet$.pipe(takeUntil(this.destroy)).subscribe((dateRangeSet) => {
				if (dateRangeSet && dateRangeSet.startDate && dateRangeSet.endDate) {
					this.updateDateRangeISO(dateRangeSet);
					this.dateRange = dateRangeSet || new DateRange();
					this.maxDate = this.dateRange.endDate;
				}
			});
		}

		switch (this.fitParent) {
			case 'always': {
				this.tableWrapperClass = 'fit-parent';
				this.footerClass = 'fit-parent';
				break;
			}

			case 'sm': {
				this.tableWrapperClass = 'fit-parent-sm';
				this.footerClass = 'fit-parent-sm';
				break;
			}

			case 'md': {
				this.tableWrapperClass = 'fit-parent-md';
				this.footerClass = 'fit-parent-md';
				break;
			}

			case 'lg': {
				this.tableWrapperClass = 'fit-parent-lg';
				this.footerClass = 'fit-parent-lg';
				break;
			}

			case 'xl': {
				this.tableWrapperClass = 'fit-parent-xl';
				this.footerClass = 'fit-parent-xl';
				break;
			}

			default: {
				this.tableWrapperClass = '';
				this.footerClass = '';
				break;
			}
		}

		if (!this.showPaginator && !this.showRowCount) {
			this.showFooter = false;
			this.footerClass += ' hide';
		} else {
			this.showFooter = true;
		}

		if (this.selectable) {
			if (this.multiselect) {
				this.selection = new SelectionModel(true, []);
			}
		}
	}

	ngAfterViewInit() {
		this.dataSource.sortingDataAccessor = (item, property) => {
			if (typeof item[property] === 'string') {
				return item[property].trim().toLocaleLowerCase();
			}

			return this.getPropertyRaw(item, property);
		};

		setTimeout(() => {
			// TODO Fix this timeout. This function needs to run when sort and columnList are both available
			this.initializeDefaultSort();
		}, 1);

		// Only set the paginator on the datasource if the dynamic
		// table is being used with default paging. Setting the
		// paginator on the datasource for custom paging causes the
		// custom paging to fail.
		if (this.defaultPaging) {
			this.dataSource.paginator = this.paginator;
		}

		this.changePaginatorText();
	}

	ngOnDestroy() {
		super.ngOnDestroy();
	}

	setDefaultPaging() {
		this.paging = new Paging();
		this.paging.pageIndex = 0;
		this.paging.pageSize = this.defaultPageSize;
		this.paging.length = this.dataSource.data.length;
		this.paging.dataLoading = false;
	}

	loadDataSource() {
		this.checkDateFilter();

		if (this.showSearch) {
			this.search();
		}

		this.filterColumns();
		this.updateDataSource();
	}

	checkDateFilter() {
		this.showChart = false;

		// Terribly hacky, but re-rendering the component because 2-Way Binding isn't supported. It works...
		setTimeout(() => {
			this.showChart = true;
		}, 100);

		this.filterByDate(this.minDate, this.maxDate);
	}

	goToRoute(row) {
		if (this.editRoute && row[this.editColumn]) {
			this.editRoute += row[this.editColumn];
			this.router.navigate([this.editRoute]);
		}
	}

	rowClicked(event, row) {
		let shouldRoute: boolean = true;

		if (event.target.closest('mat-checkbox') || event.target.closest('mat-radio-button')) {
			shouldRoute = false;
		}

		// Check if any action button or menu trigger is clicked
		if (event) {
			// Check for any button, mat-icon-button, or menu trigger elements
			const clickedButton = event.target.closest('button');
			const clickedMatIcon = event.target.closest('mat-icon');
			const clickedInActionsCell = event.target.closest('.actions-cell');

			// If clicked element is a button or mat-icon within the actions cell, prevent routing
			if (clickedInActionsCell && (clickedButton || clickedMatIcon)) {
				return;
			}

			// Also check for any Material UI button-related elements
			if (clickedButton || event.target.closest('[mat-icon-button]') || event.target.closest('[mat-menu-trigger]')) {
				return;
			}
		}

		if (this.editRoute && shouldRoute) {
			this.goToRoute(row);
		}

		this.selection.toggle(row);

		const selectedValue = this.selectable && this.multiselect ? this.selection.selected : this.selection.selected.length > 0 ? this.selection.selected[0] : null; // If multiselect, emit array, else emit either value or null

		this.onRowClick.emit(selectedValue);
	}

	getPropertyRaw(obj, propString) {
		// This function is for sort only. It returns values as is. getPropertyMapped returns '0' instead of 0 and 'false' instead of false, because in template 0 and false are read as null
		if (!propString) return obj;

		var prop,
			props = propString.split('.');

		for (var i = 0, iLen = props.length - 1; i < iLen; i++) {
			prop = props[i];

			var candidate = obj[prop];
			if (candidate) {
				obj = candidate;
			} else {
				break;
			}
		}

		let returnValue = obj[props[i]];

		return returnValue;
	}
	
	getPropertyMapped(obj, propString) {
		// This function returns '0' instead of 0 and 'false' instead of false, because in template 0 and false are read as null
		if (!propString) return obj;

		var prop,
			props = propString.split('.');

		for (var i = 0, iLen = props.length - 1; i < iLen; i++) {
			prop = props[i];

			var candidate = obj[prop];
			if (candidate) {
				obj = candidate;
			} else {
				break;
			}
		}

		let returnValue = obj[props[i]];

		if (returnValue === false) {
			returnValue = 'false';
		}

		if (returnValue === 0) {
			returnValue = '0';
		}

		return returnValue;
	}

	setProperty(obj, column: DynamicTableColumn, value) {
		if (!column.name) return;

		if (column.type === DynamicTableType.NUMBER) {
			if (value === '') {
				value = null;
			} else {
				value = Number(value);
			}
		}

		var prop,
			props = column.name.split('.');

		for (var i = 0, iLen = props.length - 1; i < iLen; i++) {
			prop = props[i];

			var candidate = obj[prop];
			if (candidate) {
				obj = candidate;
			} else {
				break;
			}
		}

		obj[props[i]] = value;
	}

	setColumn(column: DynamicTableColumn, value) {
		if (!column.name) return;

		if (column.type === DynamicTableType.NUMBER) {
			if (value === '') {
				value = null;
			} else {
				value = Number(value);
			}
		}

		for (let i = 0; i < this.dataSource.data.length; i++) {
			this.dataSource.data[i][column.name] = value;
		}
	}

	currentLineStyleDisplay(lineStyle: LineStyle): string {
		const foundLineStyle = this.lineStyleOptions.find((l) => l.lineStyle === lineStyle);

		if (foundLineStyle) {
			return foundLineStyle.label;
		}

		return this.lineStyleOptions[0].label;
	}

	isArray(value: any) {
		return Array.isArray(value);
	}

	search() {
		this.filteredItems = this.items?.filter((item) => {
			let concatString = '';
			for (var i of this.columnList.filter((c) => !c.hide)) {
				let prop = this.getPropertyMapped(item, i.name);
				if (prop) concatString += prop;
			}
			return concatString.toLowerCase().includes(this.searchTerm.toLowerCase());
		});
		this.updateDataSource();
	}

	updateDataSource() {
		this.dataSource.data = this.filteredItems;
		if (this.highlightNewItems) {
			if ((this.oldItems && this.oldItems.length > 0) || this.dataInitialized) {
				this.filteredItems.forEach((item, index) => {
					if (!this.oldItems.some((oldItem) => UtilityHelper.isEqualSimple(item, oldItem))) {
						document.getElementsByClassName('mat-mdc-row')[index].classList.add('highlighted');

						setTimeout(() => {
							document.getElementsByClassName('mat-mdc-row')[index].classList.remove('highlighted');
						}, 2000);
					}
				});
			}

			// TODO write code to highlight first added item and differentiate between initial push

			this.oldItems = UtilityHelper.deepCopy(this.filteredItems);
		}

		if (this.items) {
			this.dataInitialized = true; // This sets the flag for when initial data is already passed it. Anything after this will be updates
			this.hideTableSpinner();
		}
	}

	filterColumns() {
		this.columnNames = [];

		if (this.draggable) {
			this.columnNames.push('drag');
			this.disableSorting = true;
		}

		if (this.selectable) {
			if (this.multiselect) {
				this.columnNames.push('multiRowSelect');
				this.disableSorting = true;
			} else {
				this.columnNames.push('singleRowSelect');
				this.disableSorting = true;
			}
		}

		this.columnList.forEach((c) => {
			if (!c.hide) this.columnNames.push(c.name);
		});

		if ((this.menuButtons.length > 0 || this.addHeaderButton) && !this.hideMenuButtons) {
			this.columnNames.push('actions');
		}
	}

	columnShown(column: DynamicTableColumn) {
		return this.columnNames.includes(column.name);
	}

	changeColumnsShown(checked: boolean, column: DynamicTableColumn) {
		column.hide = !checked;
		this.filterColumns();
	}

	triggerExternalAdd(event?) {
		if (event) {
			this.onAdd.emit(event);
		} else {
			this.onAdd.emit(true);
		}
	}

	getColor(row, col: DynamicTableColumn) {
		if (col.textColor?.length > 0) {
			const color = col.textColor.find((colorMatch) => colorMatch.value == this.getPropertyMapped(row, col.name));
			if (color) {
				return color.colorHex;
			}
		}
	}

	showMenuTrigger(row) {
		for (const menuButton of this.menuButtons) {
			if (this.showMenuButton(row, menuButton)) {
				return true; // Return true immediately if any flag is true
			}
		}
		return false; // Return false if all flags are false
	}

	showMenuButton(row, menuButton: DynamicMenuButton) {
		// Checks the condition, to show or hide button
		let showButton: boolean = true;

		if ((menuButton.condition && !row[menuButton.condition]) || menuButton.hide) {
			showButton = false;
		}

		return showButton;
	}

	exportDownload() {
		const { startDate, endDate } = this.dateRange;
		const startDateMillis: number = this.convertToUtcMillis(startDate.toISOString());
		const endDateMillis: number = this.convertToUtcMillis(endDate.toISOString());
		this.onDownloadEvent.emit({
			startDateMillis,
			endDateMillis,
		});
	}
	
	private convertToUtcMillis(utcDateString: string): number {
		// Parse the ISO string to a Date object (already in UTC).
		const date: Date = parseISO(utcDateString);
		
		// Return the UTC milliseconds.
		return date.getTime();
	}
	
	exportFatigueReport() {
		this.onDownloadFatigueReportEvent.emit(this.dateRange);
	}

	updateDateRange(dateRangeISO: DateRangeISOModel) {
		if (dateRangeISO?.start && dateRangeISO?.end) {
			// Convert ISO strings to Date objects (these are UTC from the picker).
			// Update local date range properties with timezone-adjusted dates.
			this.dateRange.startDate = new Date(dateRangeISO.start);
			this.dateRange.endDate = new Date(dateRangeISO.end);

			// Reset pagination & update date range service.
			this.paging.pageIndex = 0;
			this.dateRangeService.changeDateRange(this.dateRange);
		}
	}

	filterByDate(startDate: Date, endDate: Date) {
		this.filteredItems = this.items;
		if (startDate && endDate) {
			this.updateDataSource();
		}
	}

	pageChanged(event: any) {
		this.onPageChanged.emit(event);
	}

	onDrop(event: CdkDragDrop<string[]>) {
		if (event.previousIndex === event.currentIndex) {
			return;
		}

		moveItemInArray(this.items, event.previousIndex, event.currentIndex);
		this.dataSource.data = this.items;
		this.onDrag.emit(this.items);
	}

	initializeDefaultSort() {
		const defaultSortColumn: DynamicTableColumn = this.columnList.find((column: DynamicTableColumn) => column.isDefaultSortColumn && column.defaultSortDirection);

		if (defaultSortColumn) {
			this.sort.sort({
				id: defaultSortColumn.sortOnPropertyName || defaultSortColumn.name,
				start: defaultSortColumn.defaultSortDirection,
			} as MatSortable);
		}
	}

	changePaginatorText() {
		let elements = document.getElementsByClassName('mat-mdc-paginator-page-size-label'); // Find the elements
		for (var i = 0; i < elements.length; i++) {
			elements[i].innerHTML = 'Items:'; // Change the content
		}
	}

	getItemClass(column: DynamicTableColumn) {
		let classes: string = '';

		if (column.hide) {
			classes = 'd-none';
		} else {
			classes += column.hideOnExtraSmallScreen ? 'd-none ' : 'd-flex ';
			classes += column.hideOnSmallScreen ? 'd-sm-none ' : 'd-sm-flex ';
			classes += column.hideOnMediumScreen ? 'd-md-none ' : 'd-md-flex ';
			classes += column.hideOnLargeScreen ? 'd-lg-none ' : 'd-lg-flex ';
			classes += column.hideOnExtraLargeScreen ? 'd-xl-none ' : 'd-xl-flex ';
			classes = classes.trim();
		}

		if (column.disableSorting || column.type === DynamicTableType.ICON_BUTTON) {
			classes += ' sort-disabled';
		}

		return classes;
	}

	toggleSlider() {
		this.sliderToggled.emit();
	}

	hideTableSpinner() {
		setTimeout(() => {
			this.showSpinner = false;
		}, 500);
	}

	toggleAll(checked: boolean) {
		this.checkboxes.forEach((checkbox) => (checkbox.checked = checked));
		if (checked) {
			this.selection.select(...this.dataSource.data);
		} else {
			this.selection.clear();
		}

		this.onRowClick.emit(this.selection.selected);
	}
	
	private updateDateRangeISO(dateRange: DateRange): void {
		// DateRange already contains UTC dates - convert directly to ISO
		this.dateRangeISO = {
			start: dateRange.startDate.toISOString(),
			end: dateRange.endDate.toISOString(),
		};

		// Transform offset for picker (returns 0 when browser timezone matches job timezone)
		// Store in separate property - never mutate @Input properties!
		this.pickerTimezoneOffset = this.timezoneService.getOffsetFromJobTimezone(
			dateRange.startDate,
			this.utcDateTimeOffset
		);

		// Always update boundary dates from dynamicDateFilter when setting date range
		// This ensures they're set at the same time as the selected range, when offset is available
		if (this.dynamicDateFilter?.minDate) {
			this.minDateISO = this.dynamicDateFilter.minDate.toISOString();
		}
		if (this.dynamicDateFilter?.maxDate) {
			this.maxDateISO = this.dynamicDateFilter.maxDate.toISOString();
		}
	}
}
